import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { format, parseISO } from 'date-fns';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from 'recharts';
import {
  PlusIcon,
  PencilSquareIcon,
  CreditCardIcon,
  CalendarDaysIcon,
  UsersIcon,
  DocumentTextIcon,
  BanknotesIcon,
} from '@heroicons/react/24/outline';
import { getDashboard } from '../services/dashboardService';
import { createClient } from '../services/clientService';
import Badge from '../components/common/Badge';
import Modal from '../components/common/Modal';
import ClientForm from '../components/features/clients/ClientForm';
import SkeletonBlock from '../components/common/SkeletonBlock';
import EmptyState from '../components/common/EmptyState';
import toast from 'react-hot-toast';
import useThemeStore from '../store/themeStore';

const fmt = (val) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(val ?? 0);

const STATUS_VARIANTS = {
  scheduled:   'info',
  confirmed:   'success',
  completed:   'default',
  cancelled:   'error',
  no_show:     'warning',
  rescheduled: 'warning',
};

const STAT_ICONS = {
  appointments: CalendarDaysIcon,
  clients:      UsersIcon,
  notes:        DocumentTextIcon,
  balance:      BanknotesIcon,
};

function StatCard({ label, value, accentClass, to, icon: Icon }) {
  const content = (
    <div className="card-hover group">
      <div className="flex items-start justify-between">
        <p className="text-sm text-neutral-500 dark:text-neutral-400">{label}</p>
        {Icon && (
          <div className="w-8 h-8 rounded-lg bg-neutral-100 dark:bg-neutral-700 flex items-center justify-center shrink-0">
            <Icon className="w-4 h-4 text-neutral-400 dark:text-neutral-500" />
          </div>
        )}
      </div>
      <p className={`text-2xl font-semibold mt-2 ${accentClass || 'text-neutral-900 dark:text-neutral-100'}`}>
        {value}
      </p>
    </div>
  );
  return to ? <Link to={to}>{content}</Link> : content;
}

function DashboardSkeleton() {
  return (
    <div className="space-y-6">
      <SkeletonBlock className="h-8 w-40" rounded="rounded-lg" />
      <div className="flex gap-2">
        {[1, 2, 3].map((i) => <SkeletonBlock key={i} className="h-9 w-36" rounded="rounded-lg" />)}
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {[1, 2, 3, 4].map((i) => (
          <div key={i} className="card space-y-3">
            <SkeletonBlock className="h-4 w-24" rounded="rounded" />
            <SkeletonBlock className="h-8 w-16" rounded="rounded" />
          </div>
        ))}
      </div>
      <div className="card">
        <SkeletonBlock className="h-5 w-32 mb-4" rounded="rounded" />
        <SkeletonBlock className="h-[220px]" rounded="rounded-lg" />
      </div>
    </div>
  );
}

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white dark:bg-neutral-800 border border-neutral-200 dark:border-neutral-700 rounded-xl shadow-lg p-3 text-sm">
      <p className="font-medium text-neutral-900 dark:text-neutral-100 mb-1">{label}</p>
      {payload.map((entry) => (
        <p key={entry.name} style={{ color: entry.color }}>
          {entry.name === 'revenue' ? fmt(entry.value) : `${entry.value} sessions`}
        </p>
      ))}
    </div>
  );
};

export default function Dashboard() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [isNewClientOpen, setIsNewClientOpen] = useState(false);
  const { theme } = useThemeStore();

  const { data, isLoading } = useQuery({
    queryKey: ['dashboard'],
    queryFn: getDashboard,
    refetchInterval: 60_000,
  });

  const createClientMutation = useMutation({
    mutationFn: createClient,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['clients'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });
      setIsNewClientOpen(false);
      toast.success(data?.inviteSent ? 'Client created and portal invite sent' : 'Client created');
      navigate(`/clients/${data?.client?.id}`);
    },
    onError: (err) => toast.error(err.response?.data?.message || 'Failed to create client'),
  });

  if (isLoading) return <DashboardSkeleton />;

  const {
    todayAppointments = [],
    activeClientsCount = 0,
    pendingNotesCount = 0,
    outstandingBalance = '0.00',
    upcomingAppointments = [],
    recentClients = [],
    weeklyStats = [],
  } = data ?? {};

  const hasChartData = weeklyStats.some((w) => w.sessions > 0 || w.revenue > 0);
  const gridColor = theme === 'dark' ? '#3f3f46' : '#f4f4f5';
  const axisColor = theme === 'dark' ? '#71717a' : '#a1a1aa';

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1>Dashboard</h1>
      </div>

      {/* Quick actions */}
      <div className="flex flex-wrap gap-2">
        <button onClick={() => navigate('/appointments')} className="btn btn-primary">
          <PlusIcon className="w-4 h-4" />
          New appointment
        </button>
        <button onClick={() => setIsNewClientOpen(true)} className="btn btn-secondary">
          <UsersIcon className="w-4 h-4" />
          New client
        </button>
        <button onClick={() => navigate('/notes/new')} className="btn btn-secondary">
          <PencilSquareIcon className="w-4 h-4" />
          Write note
        </button>
        <button onClick={() => navigate('/billing/new')} className="btn btn-secondary">
          <CreditCardIcon className="w-4 h-4" />
          New invoice
        </button>
      </div>

      {/* Stat cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          label="Today's appointments"
          value={todayAppointments.length}
          icon={STAT_ICONS.appointments}
          to="/appointments"
        />
        <StatCard
          label="Active clients"
          value={activeClientsCount}
          icon={STAT_ICONS.clients}
          to="/clients"
        />
        <StatCard
          label="Unsigned notes"
          value={pendingNotesCount}
          icon={STAT_ICONS.notes}
          accentClass={pendingNotesCount > 0 ? 'text-warning-600 dark:text-warning-400' : ''}
          to="/notes"
        />
        <StatCard
          label="Outstanding balance"
          value={fmt(outstandingBalance)}
          icon={STAT_ICONS.balance}
          accentClass={parseFloat(outstandingBalance) > 0 ? 'text-danger-600 dark:text-danger-400' : ''}
          to="/billing"
        />
      </div>

      {/* Weekly activity chart */}
      <div className="card">
        <h2 className="mb-4">Activity (last 8 weeks)</h2>
        {hasChartData ? (
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={weeklyStats} margin={{ top: 4, right: 16, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={gridColor} />
              <XAxis
                dataKey="week"
                tick={{ fontSize: 11, fill: axisColor }}
                axisLine={false}
                tickLine={false}
              />
              <YAxis
                yAxisId="sessions"
                orientation="left"
                tick={{ fontSize: 11, fill: axisColor }}
                axisLine={false}
                tickLine={false}
                allowDecimals={false}
                width={28}
              />
              <YAxis
                yAxisId="revenue"
                orientation="right"
                tick={{ fontSize: 11, fill: axisColor }}
                axisLine={false}
                tickLine={false}
                tickFormatter={(v) => `$${v}`}
                width={48}
              />
              <Tooltip content={<CustomTooltip />} />
              <Legend
                wrapperStyle={{ fontSize: 12, paddingTop: 8 }}
                formatter={(name) => name === 'sessions' ? 'Sessions' : 'Revenue'}
              />
              <Bar yAxisId="sessions" dataKey="sessions" fill="#6366f1" radius={[3, 3, 0, 0]} maxBarSize={32} />
              <Bar yAxisId="revenue" dataKey="revenue" fill="#10b981" radius={[3, 3, 0, 0]} maxBarSize={32} />
            </BarChart>
          </ResponsiveContainer>
        ) : (
          <EmptyState
            icon={CalendarDaysIcon}
            compact
            title="No activity yet"
            description="Completed sessions and payments will appear here."
          />
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Today's schedule */}
        <div className="card">
          <div className="flex items-center justify-between mb-4">
            <h2>Today — {format(new Date(), 'EEEE, MMM d')}</h2>
            <Link to="/appointments" className="text-sm text-primary-600 hover:text-primary-700">
              View all
            </Link>
          </div>
          {todayAppointments.length === 0 ? (
            <EmptyState
              icon={CalendarDaysIcon}
              compact
              title="No appointments today"
            />
          ) : (
            <div className="space-y-3">
              {todayAppointments.map((appt) => (
                <div
                  key={appt.id}
                  className="flex items-center gap-3 group cursor-pointer rounded-lg p-1.5 -mx-1.5 hover:bg-neutral-50 dark:hover:bg-neutral-700/50 transition-colors"
                  onClick={() => navigate('/appointments')}
                >
                  <div
                    className="w-1 h-10 rounded-full shrink-0"
                    style={{ backgroundColor: appt.appointmentType?.color || '#6366f1' }}
                  />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-neutral-900 dark:text-neutral-100">
                      {appt.client ? `${appt.client.firstName} ${appt.client.lastName}` : 'Unknown client'}
                    </p>
                    <p className="text-xs text-neutral-500 dark:text-neutral-400">
                      {format(parseISO(appt.startTime), 'h:mm a')}
                      {appt.appointmentType && ` · ${appt.appointmentType.name}`}
                    </p>
                  </div>
                  <Badge
                    label={appt.status.replace('_', ' ')}
                    variant={STATUS_VARIANTS[appt.status] || 'default'}
                  />
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Upcoming appointments */}
        <div className="card">
          <div className="flex items-center justify-between mb-4">
            <h2>Upcoming</h2>
            <Link to="/appointments" className="text-sm text-primary-600 hover:text-primary-700">
              View all
            </Link>
          </div>
          {upcomingAppointments.length === 0 ? (
            <EmptyState
              icon={CalendarDaysIcon}
              compact
              title="No upcoming appointments"
            />
          ) : (
            <div className="space-y-3">
              {upcomingAppointments.map((appt) => (
                <div key={appt.id} className="flex items-center gap-3">
                  <div className="text-xs text-neutral-400 dark:text-neutral-500 w-14 shrink-0 text-right">
                    <div>{format(parseISO(appt.startTime), 'MMM d')}</div>
                    <div>{format(parseISO(appt.startTime), 'h:mm a')}</div>
                  </div>
                  <div
                    className="w-1 h-10 rounded-full shrink-0"
                    style={{ backgroundColor: appt.appointmentType?.color || '#6366f1' }}
                  />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-neutral-900 dark:text-neutral-100 truncate">
                      {appt.client ? `${appt.client.firstName} ${appt.client.lastName}` : 'Unknown client'}
                    </p>
                    {appt.appointmentType && (
                      <p className="text-xs text-neutral-500 dark:text-neutral-400">{appt.appointmentType.name}</p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Recent clients */}
        {recentClients.length > 0 && (
          <div className="card">
            <div className="flex items-center justify-between mb-4">
              <h2>Recent clients</h2>
              <Link to="/clients" className="text-sm text-primary-600 hover:text-primary-700">
                View all
              </Link>
            </div>
            <div className="space-y-2">
              {recentClients.map((client) => (
                <div key={client.id} className="flex items-center justify-between text-sm">
                  <Link
                    to={`/clients/${client.id}`}
                    className="text-primary-600 hover:text-primary-700 hover:underline font-medium"
                  >
                    {client.firstName} {client.lastName}
                  </Link>
                  <Badge label={client.status} variant={client.status} />
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* New Client Modal */}
      <Modal isOpen={isNewClientOpen} onClose={() => setIsNewClientOpen(false)} title="New Client" size="lg">
        <ClientForm
          onSubmit={(data) => createClientMutation.mutate(data)}
          onCancel={() => setIsNewClientOpen(false)}
          isSubmitting={createClientMutation.isPending}
        />
      </Modal>
    </div>
  );
}
